import { useEffect } from "react";

/**
 * Locks page (html/body) scrolling while `locked` is true and restores the
 * previous inline styles on unlock/unmount.
 *
 * Use it on full-screen views (e.g. the location screen) or while an overlay is
 * open, so the *document* can't scroll and slide fixed/sticky chrome (header,
 * order-type tabs, footer CTAs) out of view — the exact glitch reported when a
 * `100vh`-sized pane overshoots the visible viewport on mobile Safari. Internal
 * scroll containers (elements with their own `overflow`) keep scrolling
 * normally; only the page behind them is pinned.
 */
export const useScrollLock = (locked: boolean) => {
  useEffect(() => {
    if (typeof document === "undefined" || !locked) {
      return;
    }

    const { body } = document;
    const html = document.documentElement;

    const previous = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    };

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    // Stop iOS rubber-band scrolling of the page behind the fixed panes.
    body.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previous.bodyOverflow;
      html.style.overflow = previous.htmlOverflow;
      body.style.overscrollBehavior = previous.bodyOverscroll;
    };
  }, [locked]);
};
